import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { sanitizeDocument, sanitizeDocumentStream, continueSanitizeDocumentStream, checkTearlineMosaicRisk, tearlineUploadStream, tearlineWhatIf } from '../services/api';
import { useClassificationLabels } from '../contexts/ClassificationLabelsContext';
import FileUpload from '../components/FileUpload';
import ClassificationBadge from '../components/ClassificationBadge';
import ProcessingStatus from '../components/ProcessingStatus';
import SanitizationReasoning from '../components/SanitizationReasoning';
import AuthorityReviewFlag from '../components/AuthorityReviewFlag';
import SCGRuleHoppingGraph from '../components/SCGRuleHoppingGraph';
import TrackChangesViewer from '../components/TrackChangesViewer';
import {
  DocumentTextIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function Tearline() {
  const location = useLocation();
  const [textInput, setTextInput] = useState('');
  const [targetClassification, setTargetClassification] = useState('UNCLASSIFIED');
  const [targetCaveats, setTargetCaveats] = useState([]);
  const [targetReleasability, setTargetReleasability] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showCountrySelect, setShowCountrySelect] = useState(false);
  const [showGroupSelect, setShowGroupSelect] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [maxIterations, setMaxIterations] = useState(5);
  const [tearlineResult, setTearlineResult] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [showOriginalText, setShowOriginalText] = useState(false);
  const [streamingIterations, setStreamingIterations] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentIteration, setCurrentIteration] = useState(null);
  const [strategyResults, setStrategyResults] = useState([]);
  const [preClassifiedDocument, setPreClassifiedDocument] = useState(null);
  const [isContinueProcessing, setIsContinueProcessing] = useState(false);
  const [showRuleHoppingGraph, setShowRuleHoppingGraph] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  const [sanitizationMode, setSanitizationMode] = useState('standard');
  const [retrievalMode, setRetrievalMode] = useState(''); // '' = system default
  const [resultView, setResultView] = useState('standard'); // 'standard' or 'track_changes'
  const [mosaicResult, setMosaicResult] = useState(null);
  const [mosaicLoading, setMosaicLoading] = useState(false);
  const [inputMode, setInputMode] = useState('text'); // 'text' | 'file'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [declassBlockCopied, setDeclassBlockCopied] = useState(false);
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [whatIfLoading, setWhatIfLoading] = useState(false);
  const [disabledChanges, setDisabledChanges] = useState(new Set());
  const timerRef = useRef(null);

  // Sanitization mode options
  const sanitizationModes = [
    {
      value: 'standard',
      label: 'Standard',
      description: 'Original approach with abstraction-first sanitization strategies'
    },
    {
      value: 'citation_required',
      label: 'Citation Required',
      description: 'Requires citing specific SCG rules for each change with detailed explanations. Maximizes content preservation.'
    },
    {
      value: 'two_phase',
      label: 'Two-Phase Analysis',
      description: 'First identifies all classification triggers (atomic + combinatorial), then applies targeted minimal changes.'
    }
  ];

  // Classification labels from backend API (with fallback)
  const labels = useClassificationLabels();

  // Derive arrays from context metadata
  const classificationLevels = labels.classification_levels || ['UNCLASSIFIED', 'CONFIDENTIAL', 'SECRET', 'TOP SECRET'];
  const compartmentCategories = labels.compartment_categories || {};
  const compartmentDescriptions = labels.compartment_descriptions || {};
  const disseminationDescriptions = labels.dissemination_descriptions || {};
  const availableCaveats = labels.compartments || [];
  const availableReleasability = labels.dissemination_controls || [];
  const availableCountries = useMemo(
    () => Object.keys(labels.country_codes || {}),
    [labels.country_codes]
  );
  const countryNames = labels.country_codes || {};
  const availableGroups = useMemo(
    () => Object.keys(labels.special_groups || {}),
    [labels.special_groups]
  );
  const groupDescriptions = labels.special_groups || {};

  // Collapsible section state for compartment categories
  const [expandedCategories, setExpandedCategories] = useState({});

  // Classification presets
  const PRESETS = [
    { label: 'UNCLASSIFIED', level: 'UNCLASSIFIED', caveats: [], releasability: [], countries: [], groups: [] },
    { label: 'SECRET//NOFORN', level: 'SECRET', caveats: [], releasability: ['NOFORN'], countries: [], groups: [] },
    { label: 'SECRET//REL TO FVEY', level: 'SECRET', caveats: [], releasability: [], countries: [], groups: ['FVEY'] },
    { label: 'CONFIDENTIAL//FOUO', level: 'CONFIDENTIAL', caveats: [], releasability: ['FOUO'], countries: [], groups: [] },
  ];

  // Validation helpers
  const compartmentsDisabled = targetClassification === 'UNCLASSIFIED' || targetClassification === 'CONFIDENTIAL';
  const hasNofornRelToConflict = targetReleasability.includes('NOFORN') && (selectedGroups.length > 0 || selectedCountries.length > 0);

  // Check for text from classification redirect, pre-classified documents, or restored history
  useEffect(() => {
    const savedText = sessionStorage.getItem('tearlineText');
    if (savedText) {
      setTextInput(savedText);
      sessionStorage.removeItem('tearlineText'); // Clear after use
    }

    // Handle navigation state
    if (location.state) {
      // Handle restored query from history
      const { restoredQuery, classifiedDocument, documentClassification, fullClassification } = location.state;

      if (restoredQuery && restoredQuery.query_type === 'sanitization') {
        // Restore input text
        if (restoredQuery.input_text) {
          setTextInput(restoredQuery.input_text);
        }

        // Restore input parameters
        if (restoredQuery.input_params) {
          if (restoredQuery.input_params.targetClassification) {
            setTargetClassification(restoredQuery.input_params.targetClassification);
          }
          if (restoredQuery.input_params.targetCaveats) {
            setTargetCaveats(restoredQuery.input_params.targetCaveats);
          }
          if (restoredQuery.input_params.targetReleasability) {
            setTargetReleasability(restoredQuery.input_params.targetReleasability);
          }
          if (restoredQuery.input_params.maxIterations) {
            setMaxIterations(restoredQuery.input_params.maxIterations);
          }
          if (restoredQuery.input_params.sanitizationMode) {
            setSanitizationMode(restoredQuery.input_params.sanitizationMode);
          }
          if (restoredQuery.input_params.retrieval_mode) {
            setRetrievalMode(restoredQuery.input_params.retrieval_mode);
          }
        }

        // Restore results
        if (restoredQuery.result) {
          setTearlineResult(restoredQuery.result);

          // Restore iteration results if available
          if (restoredQuery.result.iteration_results) {
            setStreamingIterations(restoredQuery.result.iteration_results);
          }
        }

        // Clear the location state to prevent re-restoration on refresh
        window.history.replaceState({}, document.title);
      }
      // Handle pre-classified document from classification redirect
      else if (classifiedDocument) {
        setTextInput(classifiedDocument);
        setPreClassifiedDocument({
          text: classifiedDocument,
          classification: documentClassification,
          fullClassification: fullClassification,
          timestamp: Date.now()
        });

        // Show optimization message
        setLoadingMessage('Using cached classification results for faster processing...');
      }
    }
  }, [location.state]);

  // Mosaic auto-check disabled — UI was confusing for users
  // useEffect(() => {
  //   if (tearlineResult?.final_text && tearlineResult?.original_text && !tearlineResult?.track_changes_applied && !isStreaming) {
  //     handleAutoMosaicCheck(tearlineResult.final_text);
  //   }
  // }, [tearlineResult?.final_text, isStreaming]);

  // Live processing timer
  useEffect(() => {
    if (isStreaming) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => setElapsedTime(prev => prev + 0.1), 100);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isStreaming]);

  const handleStreamingSanitization = async (text, targetClassification, targetCaveats, targetReleasability, maxIterations, mode = 'standard') => {
    setTearlineResult(null);
    setStreamingIterations([]);
    setCurrentIteration(null);
    setStrategyResults([]);
    setMosaicResult(null);
    setWhatIfResult(null);
    setWhatIfLoading(false);
    setDisabledChanges(new Set());
    setIsStreaming(true);
    setIsLocalLoading(true);
    setLoadingProgress(0);
    setLoadingMessage(`Starting tearline sanitization (${mode} mode)...`);

    try {
      const finalResult = await sanitizeDocumentStream(
        text,
        targetClassification,
        targetCaveats,
        targetReleasability,
        maxIterations,
        (update) => {
          if (update.progress !== undefined) {
            setLoadingProgress(update.progress);
          }
          if (update.message) {
            setLoadingMessage(update.message);
          }
          
          // Force a re-render by updating a timestamp
          setCurrentIteration(Date.now());
          
          // Handle different update types
          switch (update.status) {
            case 'initializing':
              setLoadingMessage('Gathering SCG rules and classifying original document...');
              break;
              
            case 'initial_classification_complete':
              setLoadingMessage(`Original: ${update.original_classification?.full_classification || 'Unknown'}`);
              break;
              
            case 'iteration_starting':
              setCurrentIteration(update.iteration);
              setLoadingMessage(`Starting iteration ${update.iteration}/${update.max_iterations}...`);
              break;
              
            case 'generating_strategies':
              setLoadingMessage(`Generating sanitization strategies for iteration ${update.iteration}...`);
              break;
              
            case 'classifying_strategies':
              setLoadingMessage(`Testing ${update.strategies_count} sanitization strategies...`);
              setStrategyResults([]); // Reset for new iteration
              break;
              
            case 'strategy_complete':
              setStrategyResults(prev => [...prev, {
                type: 'success',
                name: update.strategy_name,
                classification: update.classification_result?.full_classification || 'Unknown',
                sanitized_text: update.sanitized_text,
                completed: update.completed_strategies,
                total: update.total_strategies
              }]);
              setLoadingMessage(`Strategy '${update.strategy_name}': ${update.classification_result?.full_classification || 'Unknown'} (${update.completed_strategies}/${update.total_strategies})`);
              break;
              
            case 'strategy_failed':
              setStrategyResults(prev => [...prev, {
                type: 'error',
                name: update.strategy_name,
                error: update.error,
                completed: update.completed_strategies,
                total: update.total_strategies
              }]);
              setLoadingMessage(`Strategy '${update.strategy_name}' failed: ${update.error}`);
              break;
              
            case 'iteration_complete':
              // Add completed iteration to the list
              if (update.iteration_result) {
                setStreamingIterations(prev => [...prev, update.iteration_result]);
              }
              setLoadingMessage(`Iteration ${update.iteration} complete - continuing...`);
              break;
              
            case 'completed':
              setTearlineResult(update.final_result);
              setLoadingMessage('Tearline sanitization completed successfully!');
              break;
              
            case 'max_iterations_reached':
              setTearlineResult(update.final_result);
              setLoadingMessage('Maximum iterations reached - target not fully achieved');
              break;
              
            case 'error':
              throw new Error(update.error || 'Streaming sanitization failed');
              
            default:
              if (update.message) {
                setLoadingMessage(update.message);
              }
          }
        },
        preClassifiedDocument,
        mode,
        retrievalMode || null
      );

      if (finalResult) {
        setTearlineResult(finalResult);
      }
      
    } catch (error) {
      console.error('Streaming sanitization failed:', error);
      setLoadingMessage(`Sanitization failed: ${error.message}`);
    } finally {
      setIsStreaming(false);
      setIsLocalLoading(false);
    }
  };

  const handleContinueProcessing = async () => {
    if (!tearlineResult) return;
    
    setIsContinueProcessing(true);
    setIsLocalLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('Continuing processing for failed segments...');

    try {
      // Build complete releasability array including REL TO
      const completeReleasability = [...targetReleasability];
      const relToString = buildRelToString();
      if (relToString) {
        completeReleasability.push(relToString);
      }

      const finalResult = await continueSanitizeDocumentStream(
        targetClassification,
        targetCaveats,
        completeReleasability,
        maxIterations,
        tearlineResult,
        (update) => {
          
          if (update.progress !== undefined) {
            setLoadingProgress(update.progress);
          }
          if (update.message) {
            setLoadingMessage(update.message);
          }
          
          // Handle different update types for continue processing
          switch (update.status) {
            case 'continuing':
              setLoadingMessage('Resuming sanitization for failed segments...');
              break;
              
            case 'segment_processing':
              setLoadingMessage(`Processing segment ${update.segment_id + 1}...`);
              break;
              
            case 'iteration_starting':
              setLoadingMessage(`Segment ${update.segment_id + 1} - Iteration ${update.iteration}/${update.max_iterations}...`);
              break;
              
            case 'completed':
              setTearlineResult(update.final_result);
              setLoadingMessage('Continue processing completed successfully!');
              break;
              
            case 'error':
              throw new Error(update.error || 'Continue processing failed');
              
            default:
              if (update.message) {
                setLoadingMessage(update.message);
              }
          }
        },
        sanitizationMode,
        retrievalMode || null
      );

      if (finalResult) {
        setTearlineResult(finalResult);
      }

    } catch (error) {
      console.error('Continue processing failed:', error);
      setLoadingMessage(`Continue processing failed: ${error.message}`);
    } finally {
      setIsContinueProcessing(false);
      setIsLocalLoading(false);
    }
  };

  const sanitizeMutation = useMutation({
    mutationFn: ({ text, targetClassification, targetCaveats, targetReleasability, maxIterations }) => {
      return sanitizeDocument(text, targetClassification, targetCaveats, targetReleasability, maxIterations);
    },
    onSuccess: (data) => {
      setTearlineResult(data);
    },
    onError: (error) => {
      console.error('Sanitization failed:', error);
    }
  });

  const handleAutoMosaicCheck = async (sanitizedText) => {
    if (!tearlineResult?.original_text || !sanitizedText) return;
    setMosaicLoading(true);
    try {
      const result = await checkTearlineMosaicRisk(tearlineResult.original_text, sanitizedText);
      setMosaicResult(result);
    } catch (err) {
      console.error('Mosaic check failed:', err);
      setMosaicResult(null);
    } finally {
      setMosaicLoading(false);
    }
  };

  const handleApplyTrackChanges = ({ resultText, acceptedCount, rejectedCount, pendingCount }) => {
    setTearlineResult(prev => ({
      ...prev,
      final_text: resultText,
      track_changes_applied: true,
      track_changes_summary: { acceptedCount, rejectedCount, pendingCount }
    }));
  };

  const handleWhatIfToggle = async (index) => {
    const newDisabled = new Set(disabledChanges);
    if (newDisabled.has(index)) newDisabled.delete(index);
    else newDisabled.add(index);
    setDisabledChanges(newDisabled);

    setWhatIfLoading(true);
    try {
      const changes = (tearlineResult.track_changes || []).map((c, i) => ({
        original: c.original_text || c.deleted || '',
        replacement: c.replacement_text || c.inserted || '',
        index: i,
      }));
      const result = await tearlineWhatIf(
        textInput || tearlineResult.original_text,
        tearlineResult.final_text,
        changes,
        Array.from(newDisabled)
      );
      setWhatIfResult(result);
    } catch (err) {
      console.error('What-if failed:', err);
    } finally {
      setWhatIfLoading(false);
    }
  };

  const handleCopyDeclassBlock = async () => {
    if (!tearlineResult?.declassification_block) return;
    try {
      await navigator.clipboard.writeText(tearlineResult.declassification_block);
      setDeclassBlockCopied(true);
      setTimeout(() => setDeclassBlockCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy declassification block:', err);
    }
  };

  const handleExportTXT = () => {
    if (!tearlineResult) return;
    const summary = tearlineResult.executive_summary;
    const lines = [
      '=== DECLASS TEARLINE SANITIZATION REPORT ===',
      `Date: ${new Date().toISOString()}`,
      `Mode: ${sanitizationMode}`,
      '',
      '--- CLASSIFICATION ---',
      `Original: ${tearlineResult.original_classification?.full_classification || tearlineResult.original_classification?.classification || 'N/A'}`,
      `Target: ${targetClassification}`,
      `Final: ${tearlineResult.final_classification?.full_classification || tearlineResult.final_classification?.classification || 'N/A'}`,
      `Status: ${tearlineResult.success ? 'TARGET ACHIEVED' : 'PARTIAL'}`,
      '',
    ];
    if (summary) {
      lines.push('--- EXECUTIVE SUMMARY ---');
      lines.push(summary.summary_text || '');
      lines.push('');
      lines.push(`Total Changes: ${summary.total_changes || 0}`);
      const reductionPct = summary.text_reduction_percent || 0;
      lines.push(reductionPct < 0 ? `Text Expanded: ${Math.abs(reductionPct).toFixed(1)}%` : `Text Reduction: ${reductionPct}%`);
      lines.push(`SCG Rules Cited: ${summary.scg_rules_cited || 0}`);
      lines.push(`Processing Time: ${summary.processing_time_seconds || 0}s`);
      lines.push('');
    }
    if (tearlineResult.declassification_block) {
      lines.push('--- DECLASSIFICATION AUTHORITY BLOCK ---');
      lines.push(tearlineResult.declassification_block);
      lines.push('');
    }
    lines.push('--- SANITIZED TEXT ---');
    lines.push(tearlineResult.final_text || '');
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tearline-report-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    if (!tearlineResult) return;
    const blob = new Blob([JSON.stringify(tearlineResult, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tearline-result-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUploadStream = async (file) => {
    setTearlineResult(null);
    setStreamingIterations([]);
    setCurrentIteration(null);
    setStrategyResults([]);
    setMosaicResult(null);
    setWhatIfResult(null);
    setWhatIfLoading(false);
    setDisabledChanges(new Set());
    setIsStreaming(true);
    setIsLocalLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('Uploading file and extracting text...');

    const completeReleasability = [...targetReleasability];
    const relToString = buildRelToString();
    if (relToString) completeReleasability.push(relToString);

    try {
      const finalResult = await tearlineUploadStream(
        file,
        targetClassification,
        targetCaveats,
        completeReleasability,
        maxIterations,
        (update) => {
          if (update.progress !== undefined) setLoadingProgress(update.progress);
          if (update.message) setLoadingMessage(update.message);
          if (update.status === 'completed' || update.status === 'max_iterations_reached') {
            setTearlineResult(update.final_result);
          }
          if (update.iteration_result) {
            setStreamingIterations(prev => [...prev, update.iteration_result]);
          }
        },
        sanitizationMode,
        retrievalMode || null
      );
      if (finalResult) setTearlineResult(finalResult);
    } catch (error) {
      console.error('File upload tearline failed:', error);
    } finally {
      setIsStreaming(false);
      setIsLocalLoading(false);
    }
  };

  const handleTearlineSubmit = async (e) => {
    e.preventDefault();

    if (inputMode === 'file') {
      if (!uploadedFile) return;
      handleFileUploadStream(uploadedFile);
      return;
    }

    if (!textInput.trim()) return;

    // Build complete releasability array including REL TO
    const completeReleasability = [...targetReleasability];
    const relToString = buildRelToString();
    if (relToString) {
      completeReleasability.push(relToString);
    }

    handleStreamingSanitization(
      textInput,
      targetClassification,
      targetCaveats,
      completeReleasability,
      maxIterations,
      sanitizationMode
    );
  };

  const handleCaveatToggle = (caveat) => {
    setTargetCaveats(prev => 
      prev.includes(caveat) 
        ? prev.filter(c => c !== caveat)
        : [...prev, caveat]
    );
  };

  const handleReleasabilityToggle = (marking) => {
    setTargetReleasability(prev => 
      prev.includes(marking) 
        ? prev.filter(r => r !== marking)
        : [...prev, marking]
    );
  };

  const handleCountryToggle = (country) => {
    setSelectedCountries(prev => {
      const isRemoving = prev.includes(country);
      
      if (isRemoving) {
        // If removing a country, just remove it
        return prev.filter(c => c !== country);
      } else {
        // If adding a country, add both the country and USA (if not already included)
        const newCountries = [...prev, country];
        if (!newCountries.includes('USA')) {
          newCountries.push('USA');
        }
        return newCountries;
      }
    });
  };

  const handleGroupToggle = (group) => {
    setSelectedGroups(prev => 
      prev.includes(group) 
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const buildRelToString = () => {
    const parts = [];
    if (selectedGroups.length > 0) {
      parts.push(...selectedGroups);
    }
    if (selectedCountries.length > 0) {
      parts.push(...selectedCountries);
    }
    return parts.length > 0 ? `REL TO ${parts.join(', ')}` : '';
  };

  const buildTargetClassificationDisplay = () => {
    // Build with proper IC marking format:
    // Double slash (//) between categories, single slash (/) within categories
    const categories = [targetClassification];
    if (targetCaveats.length > 0) categories.push(targetCaveats.join('/'));

    // Dissemination controls
    const dissemParts = [...targetReleasability];
    const relToString = buildRelToString();
    if (relToString) dissemParts.push(relToString);
    if (dissemParts.length > 0) categories.push(dissemParts.join('/'));

    return categories.join('//');
  };

  // Handle classification level change with smart validation
  const handleLevelChange = (newLevel) => {
    setTargetClassification(newLevel);
    // Auto-clear compartments when level drops below SECRET
    if (newLevel === 'UNCLASSIFIED' || newLevel === 'CONFIDENTIAL') {
      setTargetCaveats([]);
    }
  };

  // Apply a classification preset
  const handlePresetApply = (preset) => {
    setTargetClassification(preset.level);
    setTargetCaveats(preset.caveats);
    setTargetReleasability(preset.releasability);
    setSelectedCountries(preset.countries);
    setSelectedGroups(preset.groups);
  };

  // Toggle a compartment category section open/closed
  const handleCategoryToggle = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  // Filter countries based on search term (search by code or full name)
  const filteredCountries = availableCountries.filter(country => {
    const term = countrySearchTerm.toLowerCase();
    const name = (countryNames[country] || '').toLowerCase();
    return country.toLowerCase().includes(term) || name.includes(term);
  });

  // Function to open SCG Rule Hopping Graph modal
  const openRuleHoppingGraph = (segmentIndex = null) => {
    setSelectedSegmentIndex(segmentIndex);
    setShowRuleHoppingGraph(true);
  };

  const closeRuleHoppingGraph = () => {
    setShowRuleHoppingGraph(false);
    setSelectedSegmentIndex(null);
  };


  const isLoading = isLocalLoading || isStreaming || isContinueProcessing;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Processing Status Banner */}
      <ProcessingStatus
        isProcessing={isLoading}
        progress={loadingProgress}
        message={isStreaming ? `${loadingMessage} (${elapsedTime.toFixed(1)}s)` : loadingMessage}
      />
      
      {/* Real-time Strategy Results */}
      {isStreaming && strategyResults.length > 0 && (
        <div className="mb-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Strategy Testing Progress ({currentIteration ? `Iteration ${currentIteration}` : 'In Progress'})
          </h3>
          <div className="space-y-3">
            {strategyResults.map((strategy, index) => (
              <div key={index} className={`p-3 rounded-lg border ${
                strategy.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      strategy.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {strategy.name}
                    </span>
                    {strategy.type === 'success' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : (
                      <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {strategy.completed}/{strategy.total}
                  </span>
                </div>
                <div className="mt-2 text-sm">
                  {strategy.type === 'success' ? (
                    <div>
                      <span className="font-medium text-gray-700">Result: </span>
                      <span className="text-gray-900">{strategy.classification}</span>
                    </div>
                  ) : (
                    <div className="text-red-700">
                      <span className="font-medium">Error: </span>
                      {strategy.error}
                    </div>
                  )}
                </div>
                {strategy.type === 'success' && strategy.sanitized_text && (
                  <details className="mt-2">
                    <summary className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                      View Sanitized Text
                    </summary>
                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-800 max-h-32 overflow-y-auto">
                      {strategy.sanitized_text}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Document Tearline</h1>
        <p className="mt-2 text-gray-600">
          Sanitize documents to meet specific classification requirements through iterative refinement
        </p>
      </div>

      {/* Results Section */}
      {tearlineResult && (
        <div className="mb-8 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {tearlineResult.success ? (
                <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
              ) : (
                <ExclamationCircleIcon className="w-6 h-6 text-yellow-500 mr-2" />
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                Tearline {tearlineResult.success ? 'Completed' : 'Partial'}
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold bg-indigo-100 text-indigo-800">
                  {tearlineResult.processing_time?.toFixed(1) || '?'}s
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  {tearlineResult.total_iterations} iteration{tearlineResult.total_iterations !== 1 ? 's' : ''}
                </span>
              </h3>
            </div>

            <div className="flex items-center gap-2">
              {tearlineResult?.provenance_decision_id && (
                <a
                  href={`/provenance/${encodeURIComponent(tearlineResult.provenance_decision_id)}`}
                  className="inline-flex items-center px-3 py-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                >
                  View provenance
                </a>
              )}
              {/* SCG Rule Hopping Visualization Button */}
              <button
                onClick={() => openRuleHoppingGraph()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
                View Rule Hopping Flow
              </button>
            </div>
          </div>

          {/* Classification Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Original Classification</h4>
              <ClassificationBadge
                classification={tearlineResult.original_classification.classification}
                fullClassification={tearlineResult.original_classification.full_classification}
                caveats={tearlineResult.original_classification.caveats || []}
                releasability={tearlineResult.original_classification.releasability || []}
                confidence={tearlineResult.original_classification.confidence}
              />
            </div>

            <div className="flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-gray-400" />
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {tearlineResult.success ? 'Achieved' : 'Target'} Classification
              </h4>
              <div className="space-y-2">
                <ClassificationBadge
                  classification={tearlineResult.success ? tearlineResult.final_classification.classification : targetClassification}
                  fullClassification={tearlineResult.success ? tearlineResult.final_classification.full_classification : buildTargetClassificationDisplay()}
                  caveats={tearlineResult.success ? (tearlineResult.final_classification.caveats || []) : targetCaveats}
                  releasability={tearlineResult.success ? (tearlineResult.final_classification.releasability || []) : targetReleasability}
                  confidence={tearlineResult.success ? tearlineResult.final_classification.confidence : null}
                />
                <AuthorityReviewFlag
                  reasoning={tearlineResult.final_reasoning || tearlineResult.reasoning}
                  variant="compact"
                />
              </div>
            </div>
          </div>

          {/* Declassification Authority Block (32 CFR 2001.22) */}
          {tearlineResult.declassification_block && (
            <div className="mb-6 bg-gray-900 border border-amber-700/50 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-800 border-b border-amber-700/50 flex items-center justify-between">
                <div className="flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 text-amber-400 mr-2" />
                  <h4 className="text-sm font-semibold text-amber-300">Declassification Authority Block</h4>
                  <span className="ml-2 text-xs text-gray-400">32 CFR 2001.22</span>
                </div>
                <button
                  onClick={handleCopyDeclassBlock}
                  className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded border transition-colors ${
                    declassBlockCopied
                      ? 'bg-green-800/50 text-green-300 border-green-600'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600 border-gray-600'
                  }`}
                  title="Copy declassification block to clipboard"
                >
                  {declassBlockCopied ? (
                    <>
                      <ClipboardDocumentCheckIcon className="w-3.5 h-3.5 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="w-3.5 h-3.5 mr-1" />
                      Copy Block
                    </>
                  )}
                </button>
              </div>
              <div className="p-4">
                <div className="bg-gray-950 border border-gray-700 rounded p-4">
                  {tearlineResult.declassification_block.split('\n').map((line, idx) => {
                    const colonIdx = line.indexOf(':');
                    if (colonIdx === -1) return <div key={idx} className="font-mono text-sm text-gray-300">{line}</div>;
                    const label = line.substring(0, colonIdx + 1);
                    const value = line.substring(colonIdx + 1);
                    return (
                      <div key={idx} className="font-mono text-sm leading-relaxed">
                        <span className="text-amber-400">{label}</span>
                        <span className="text-gray-200">{value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Executive Summary — What Changed and Why */}
          {tearlineResult.executive_summary && (
            <div className="mb-6 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center">
                  <ChartBarIcon className="w-5 h-5 text-blue-400 mr-2" />
                  <h4 className="text-sm font-semibold text-blue-300">What Changed and Why</h4>
                </div>
                <div className="flex space-x-2">
                  <button onClick={handleExportTXT} className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600">
                    <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" /> Export TXT
                  </button>
                  <button onClick={handleExportJSON} className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600">
                    <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" /> Export JSON
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="text-sm text-gray-300 whitespace-pre-wrap mb-4">{tearlineResult.executive_summary.summary_text}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                    <div className="text-xl font-bold text-amber-400">{tearlineResult.executive_summary.total_changes || 0}</div>
                    <div className="text-xs text-gray-400 mt-1">Changes Made</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                    {(() => {
                      const pct = tearlineResult.executive_summary.text_reduction_percent || 0;
                      if (pct < 0) {
                        return (
                          <>
                            <div className="text-xl font-bold text-red-400">{Math.abs(pct).toFixed(1)}%</div>
                            <div className="text-xs text-red-400 mt-1">Text Expanded</div>
                          </>
                        );
                      }
                      return (
                        <>
                          <div className="text-xl font-bold text-blue-400">{pct}%</div>
                          <div className="text-xs text-gray-400 mt-1">Text Reduction</div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                    <div className="text-xl font-bold text-green-400">{tearlineResult.executive_summary.total_iterations || 0}</div>
                    <div className="text-xs text-gray-400 mt-1">Iterations</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                    <div className="text-xl font-bold text-purple-400">{tearlineResult.executive_summary.scg_rules_cited || 0}</div>
                    <div className="text-xs text-gray-400 mt-1">SCG Rules</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Continue Processing Button for Failed Segments */}
          {!tearlineResult.success && !isLoading && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">
                    Some segments didn't reach the target classification
                  </h4>
                  <p className="text-sm text-yellow-700">
                    Continue processing with additional iterations to attempt reaching the target level.
                  </p>
                </div>
                <button
                  onClick={handleContinueProcessing}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                >
                  <ClockIcon className="w-4 h-4 mr-2" />
                  Continue Processing
                </button>
              </div>
            </div>
          )}

          {/* View Toggle: Standard vs Track Changes */}
          <div className="mb-4 border-b border-gray-200">
            <nav className="flex space-x-6">
              <button
                onClick={() => setResultView('standard')}
                className={`pb-2 px-1 border-b-2 text-sm font-medium flex items-center ${
                  resultView === 'standard'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DocumentDuplicateIcon className="w-4 h-4 mr-1.5" />
                Standard View
              </button>
              <button
                onClick={() => setResultView('track_changes')}
                className={`pb-2 px-1 border-b-2 text-sm font-medium flex items-center ${
                  resultView === 'track_changes'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <PencilSquareIcon className="w-4 h-4 mr-1.5" />
                Track Changes
                {tearlineResult.track_changes && tearlineResult.track_changes.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 text-indigo-700">
                    {tearlineResult.track_changes.length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Standard View */}
          {resultView === 'standard' && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">
                  {tearlineResult.success ? 'Sanitized' : 'Best Attempt'} Document
                </h4>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowOriginalText(!showOriginalText)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4 mr-1" />
                    {showOriginalText ? 'Hide' : 'Compare'} Original
                  </button>
                </div>
              </div>

              <div className={`grid gap-4 ${showOriginalText ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                {showOriginalText && (
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1">Original Text</h5>
                    <div className="bg-red-50 border border-red-200 rounded p-3 max-h-60 overflow-y-auto">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{textInput}</p>
                    </div>
                  </div>
                )}

                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-1">
                    {tearlineResult.success ? 'Sanitized' : 'Best Attempt'} Text
                  </h5>
                  <div className={`border rounded p-3 max-h-60 overflow-y-auto ${
                    tearlineResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{tearlineResult.final_text}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Track Changes View */}
          {resultView === 'track_changes' && (
            <div className="mb-6">
              <TrackChangesViewer
                originalText={tearlineResult.original_text || textInput}
                sanitizedText={tearlineResult.final_text}
                changes={tearlineResult.track_changes || []}
                statistics={tearlineResult.diff_statistics || {}}
                onApplyChanges={handleApplyTrackChanges}
                provenanceDecisionId={tearlineResult.provenance_decision_id}
              />
              {tearlineResult.track_changes_applied && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Track changes applied: {tearlineResult.track_changes_summary?.acceptedCount || 0} accepted,{' '}
                    {tearlineResult.track_changes_summary?.rejectedCount || 0} rejected,{' '}
                    {tearlineResult.track_changes_summary?.pendingCount || 0} kept as-is.
                    The sanitized text has been updated.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* What-If Analysis Panel */}
          {tearlineResult?.track_changes?.length > 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-5 mt-4">
              <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wide mb-4">
                What-If Analysis
                {whatIfLoading && <span className="ml-2 text-xs text-gray-500 font-normal normal-case">Reclassifying...</span>}
              </h3>
              <p className="text-xs text-gray-400 mb-3">
                Toggle individual changes off to see how reverting them affects the classification.
              </p>
              {whatIfResult && (
                <div className={`mb-4 p-3 rounded border ${
                  whatIfResult.classification?.classification === (tearlineResult.target_classification || targetClassification)
                    ? 'border-green-600 bg-green-900/20' : 'border-red-600 bg-red-900/20'
                }`}>
                  <span className="text-sm font-bold text-gray-100">{whatIfResult.classification?.classification || '?'}</span>
                  <span className="text-xs text-gray-400 ml-2">
                    ({whatIfResult.enabled_count} changes active, {whatIfResult.disabled_count} reverted)
                  </span>
                  {whatIfResult.classification?.confidence && (
                    <span className="text-xs text-gray-500 ml-2">
                      Confidence: {(whatIfResult.classification.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              )}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tearlineResult.track_changes.map((change, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs p-2 rounded bg-gray-800">
                    <button
                      onClick={() => handleWhatIfToggle(i)}
                      disabled={whatIfLoading}
                      className={`flex-shrink-0 w-8 h-4 rounded-full transition-colors relative ${
                        disabledChanges.has(i) ? 'bg-gray-600' : 'bg-green-600'
                      } ${whatIfLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                        disabledChanges.has(i) ? 'left-0.5' : 'left-[18px]'
                      }`} />
                    </button>
                    <span className="text-red-400 line-through flex-1 truncate">{change.original_text || ''}</span>
                    <span className="text-green-400 flex-1 truncate">{change.replacement_text || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Mosaic Effect Risk Assessment — hidden (confusing for users) */}

      {/* Sanitization Reasoning Component */}
      {tearlineResult && (
        <div className="mb-8">
          <SanitizationReasoning
            tearlineResult={tearlineResult}
            onViewRuleFlow={openRuleHoppingGraph}
          />
        </div>
      )}

      {/* Audit Trail Card */}
      {tearlineResult && (
        <div className="mb-8 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center">
            <ClockIcon className="w-5 h-5 text-gray-400 mr-2" />
            <h4 className="text-sm font-semibold text-gray-300">Audit Trail</h4>
          </div>
          <div className="p-4">
            <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap leading-relaxed">{[
              `Timestamp:          ${new Date().toISOString()}`,
              `Mode:               ${sanitizationMode}`,
              `Method:             ${tearlineResult.method || 'parallel_segmented_streaming'}`,
              `Original Class:     ${tearlineResult.original_classification?.full_classification || tearlineResult.original_classification?.classification || 'N/A'}`,
              `Target Class:       ${targetClassification}${targetCaveats.length ? '//' + targetCaveats.join('/') : ''}`,
              `Final Class:        ${tearlineResult.final_classification?.full_classification || tearlineResult.final_classification?.classification || 'N/A'}`,
              `Target Achieved:    ${tearlineResult.success ? 'YES' : 'NO'}`,
              `Total Iterations:   ${tearlineResult.total_iterations || 0}`,
              `Processing Time:    ${tearlineResult.processing_time?.toFixed(1) || '?'}s`,
              tearlineResult.parallel_processing ? `Segments Processed: ${tearlineResult.parallel_processing.processed_segments || 0}` : null,
              tearlineResult.parallel_processing ? `Segments Successful:${tearlineResult.parallel_processing.successful_segments || 0}` : null,
              tearlineResult.parallel_processing ? `Success Rate:       ${((tearlineResult.parallel_processing.success_rate || 0) * 100).toFixed(0)}%` : null,
              `Track Changes:      ${tearlineResult.track_changes?.length || 0} changes detected`,
              tearlineResult.track_changes_applied ? `Track Changes Applied: Yes` : null,
              `SCG Rules Cited:    ${tearlineResult.executive_summary?.scg_rules_cited || 'N/A'}`,
            ].filter(Boolean).join('\n')}</pre>
          </div>
        </div>
      )}

      {/* Declassification Authority Block */}
      {tearlineResult?.declassification_block && (
        <div className="mb-8 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600 mr-2" />
              <h4 className="text-sm font-semibold text-gray-700">Declassification Authority Block</h4>
            </div>
            <span className="text-xs text-gray-500">Per 32 CFR 2001.22</span>
          </div>
          <div className="p-4">
            <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap leading-relaxed bg-gray-50 border border-gray-200 rounded p-4">
              {tearlineResult.declassification_block}
            </pre>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(tearlineResult.declassification_block)}
              className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
            >
              <ClipboardDocumentIcon className="w-3.5 h-3.5 mr-1" />
              Copy to Clipboard
            </button>
          </div>
        </div>
      )}

      {/* Input Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleTearlineSubmit}>
          {/* Document Input — Text or File Upload */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Document Input</label>
              <div className="flex rounded-md border border-gray-300 overflow-hidden">
                <button type="button" onClick={() => setInputMode('text')}
                  className={`px-3 py-1 text-xs font-medium ${inputMode === 'text' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  <DocumentTextIcon className="w-3.5 h-3.5 inline mr-1" />Text Input
                </button>
                <button type="button" onClick={() => setInputMode('file')}
                  className={`px-3 py-1 text-xs font-medium border-l border-gray-300 ${inputMode === 'file' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  <DocumentArrowDownIcon className="w-3.5 h-3.5 inline mr-1" />File Upload
                </button>
              </div>
            </div>
            {inputMode === 'text' ? (
              <textarea
                id="text-input"
                rows={8}
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Paste the document content you want to sanitize..."
              />
            ) : (
              <div>
                <FileUpload
                  onFileSelect={(file) => setUploadedFile(file)}
                  accept=".pdf,.txt,.docx,.pptx"
                />
                {uploadedFile && (
                  <div className="mt-2 flex items-center text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                    <button type="button" onClick={() => setUploadedFile(null)} className="ml-auto text-gray-400 hover:text-red-500 text-xs">Remove</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Target Classification */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Target Classification</h3>

            {/* Quick Presets */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1.5">Quick Presets</label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(preset => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handlePresetApply(preset)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      buildTargetClassificationDisplay() === preset.label
                        ? 'bg-indigo-100 border-indigo-400 text-indigo-800'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Base Classification Level — Prominent Button Group */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Classification Level</label>
              <div className="flex rounded-lg overflow-hidden border border-gray-300">
                {classificationLevels.map((level, idx) => {
                  const levelColors = {
                    'UNCLASSIFIED': 'bg-green-600 text-white border-green-700',
                    'CONFIDENTIAL': 'bg-blue-600 text-white border-blue-700',
                    'SECRET': 'bg-red-600 text-white border-red-700',
                    'TOP SECRET': 'bg-amber-500 text-black border-amber-600',
                  };
                  const isActive = targetClassification === level;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => handleLevelChange(level)}
                      className={`flex-1 px-3 py-2.5 text-sm font-semibold transition-all ${
                        idx > 0 ? 'border-l border-gray-300' : ''
                      } ${
                        isActive
                          ? levelColors[level]
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {level === 'TOP SECRET' ? 'TOP SECRET' : level}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SCI Compartments — Collapsible by Discipline */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">
                SCI Compartments
                {compartmentsDisabled && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">(requires SECRET or above)</span>
                )}
              </label>

              {compartmentsDisabled && (
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-500 mb-2">
                  <InformationCircleIcon className="w-3.5 h-3.5 inline mr-1" />
                  SCI compartments are only applicable at SECRET or TOP SECRET level.
                </div>
              )}

              <div className={`space-y-1 ${compartmentsDisabled ? 'opacity-40 pointer-events-none' : ''}`}>
                {Object.entries(compartmentCategories).map(([catKey, catInfo]) => {
                  const isExpanded = expandedCategories[catKey];
                  const catCompartments = catInfo.compartments || [];
                  const selectedInCategory = catCompartments.filter(c => targetCaveats.includes(c));
                  return (
                    <div key={catKey} className="border border-gray-200 rounded-md overflow-hidden">
                      {/* Category Header */}
                      <button
                        type="button"
                        onClick={() => handleCategoryToggle(catKey)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      >
                        <div className="flex items-center">
                          <ChevronDownIcon className={`w-4 h-4 mr-2 text-gray-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                          <span className="text-sm font-medium text-gray-700">{catInfo.label}</span>
                          {selectedInCategory.length > 0 && (
                            <span className="ml-2 bg-indigo-100 text-indigo-700 text-xs px-1.5 py-0.5 rounded-full">
                              {selectedInCategory.length}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">{catCompartments.length} markings</span>
                      </button>

                      {/* Category Body */}
                      {isExpanded && (
                        <div className="px-3 py-2 bg-white border-t border-gray-200">
                          <p className="text-xs text-gray-500 mb-2">{catInfo.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {catCompartments.map(comp => (
                              <button
                                key={comp}
                                type="button"
                                onClick={() => handleCaveatToggle(comp)}
                                title={compartmentDescriptions[comp] || comp}
                                className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                                  targetCaveats.includes(comp)
                                    ? 'bg-indigo-100 border-indigo-400 text-indigo-800'
                                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                                }`}
                              >
                                {comp}
                                {targetCaveats.includes(comp) && (
                                  <CheckCircleIcon className="w-3.5 h-3.5 ml-1 text-indigo-600" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selected compartments summary */}
              {targetCaveats.length > 0 && !compartmentsDisabled && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {targetCaveats.map(c => (
                    <span key={c} className="inline-flex items-center bg-indigo-50 text-indigo-700 text-xs px-2 py-0.5 rounded">
                      {c}
                      <button type="button" onClick={() => handleCaveatToggle(c)} className="ml-1 text-indigo-400 hover:text-indigo-600">&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Dissemination Controls */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Dissemination Controls</label>
              <div className="flex flex-wrap gap-2">
                {availableReleasability.map(marking => (
                  <button
                    key={marking}
                    type="button"
                    onClick={() => handleReleasabilityToggle(marking)}
                    title={disseminationDescriptions[marking] || marking}
                    className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                      targetReleasability.includes(marking)
                        ? 'bg-amber-100 border-amber-400 text-amber-800'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {marking}
                    {targetReleasability.includes(marking) && (
                      <CheckCircleIcon className="w-3.5 h-3.5 ml-1 text-amber-600" />
                    )}
                  </button>
                ))}
              </div>

              {/* NOFORN + REL TO conflict warning */}
              {hasNofornRelToConflict && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md flex items-start">
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-red-700">
                    <strong>Contradiction:</strong> NOFORN (not releasable to foreign nationals) conflicts with REL TO markings.
                    NOFORN will take precedence and REL TO will be removed during processing.
                  </div>
                </div>
              )}
            </div>

            {/* REL TO Section */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Releasability (REL TO)</label>

              {/* REL TO Groups */}
              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => setShowGroupSelect(!showGroupSelect)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <span>+ Add REL TO Groups (FVEY, NATO, etc.)</span>
                  <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${showGroupSelect ? '' : '-rotate-90'}`} />
                </button>
                {showGroupSelect && (
                  <div className="mt-2 flex flex-wrap gap-2 p-2 bg-gray-50 rounded">
                    {availableGroups.map(group => (
                      <button
                        key={group}
                        type="button"
                        onClick={() => handleGroupToggle(group)}
                        title={groupDescriptions[group] || group}
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                          selectedGroups.includes(group)
                            ? 'bg-green-100 border-green-400 text-green-800'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        {group}
                        {selectedGroups.includes(group) && (
                          <CheckCircleIcon className="w-3.5 h-3.5 ml-1 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* REL TO Countries */}
              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => setShowCountrySelect(!showCountrySelect)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <span>+ Add REL TO Countries</span>
                  <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${showCountrySelect ? '' : '-rotate-90'}`} />
                </button>
                {showCountrySelect && (
                  <div className="mt-2 p-2 bg-gray-50 rounded">
                    {/* Search Input */}
                    <div className="mb-2">
                      <input
                        type="text"
                        placeholder="Search countries by name or code..."
                        value={countrySearchTerm}
                        onChange={(e) => setCountrySearchTerm(e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Countries Grid */}
                    <div className="max-h-40 overflow-y-auto">
                      <div className="grid grid-cols-3 gap-1">
                        {filteredCountries.map(country => (
                          <button
                            key={country}
                            type="button"
                            onClick={() => handleCountryToggle(country)}
                            title={countryNames[country] || country}
                            className={`text-left px-2 py-1 text-xs rounded transition-colors ${
                              selectedCountries.includes(country)
                                ? 'bg-green-100 text-green-800 font-medium'
                                : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            <span className="font-medium">{country}</span>
                            <span className="ml-1 text-gray-400">{countryNames[country] ? countryNames[country].substring(0, 12) : ''}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Auto-USA Notice */}
                    {selectedCountries.length > 0 && !selectedCountries.includes('USA') && (
                      <div className="mt-2 p-1 bg-blue-50 rounded text-xs text-blue-700">
                        <InformationCircleIcon className="w-3.5 h-3.5 inline mr-1" />
                        USA will be automatically included when selecting any country.
                      </div>
                    )}

                    {/* No results message */}
                    {filteredCountries.length === 0 && countrySearchTerm && (
                      <div className="mt-2 text-xs text-gray-500 text-center">
                        No countries found matching "{countrySearchTerm}"
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* REL TO Preview */}
              {(selectedGroups.length > 0 || selectedCountries.length > 0) && (
                <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                  <span className="text-green-700 font-medium">REL TO: </span>
                  <span className="text-green-800">{buildRelToString().replace('REL TO ', '')}</span>
                </div>
              )}
            </div>

            {/* Target Preview — Live classification marking string */}
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700 font-medium">Classification Marking:</span>
                {hasNofornRelToConflict && (
                  <span className="text-xs text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" />conflict
                  </span>
                )}
              </div>
              <div className="mt-1 font-mono text-sm text-blue-900 font-semibold tracking-wide">
                {buildTargetClassificationDisplay()}
              </div>
            </div>
          </div>

          {/* Max Iterations */}
          <div className="mb-6">
            <label htmlFor="max-iterations" className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Iterations
            </label>
            <select
              id="max-iterations"
              value={maxIterations}
              onChange={(e) => setMaxIterations(parseInt(e.target.value))}
              className="block w-32 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              {[1, 2, 3, 4, 5].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          {/* Sanitization Mode */}
          <div className="mb-6">
            <label htmlFor="sanitization-mode" className="block text-sm font-medium text-gray-700 mb-2">
              Sanitization Mode
            </label>
            <div className="space-y-3">
              {sanitizationModes.map(mode => (
                <label
                  key={mode.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    sanitizationMode === mode.value
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="sanitization-mode"
                    value={mode.value}
                    checked={sanitizationMode === mode.value}
                    onChange={(e) => setSanitizationMode(e.target.value)}
                    className="mt-1 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="ml-3">
                    <span className={`font-medium ${sanitizationMode === mode.value ? 'text-indigo-900' : 'text-gray-900'}`}>
                      {mode.label}
                    </span>
                    <p className={`text-sm ${sanitizationMode === mode.value ? 'text-indigo-700' : 'text-gray-500'}`}>
                      {mode.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* SCG Retrieval Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SCG Retrieval Mode
            </label>
            <select
              value={retrievalMode}
              onChange={(e) => setRetrievalMode(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">System Default (Enhanced)</option>
              <option value="classic">Classic - Pure kNN (fastest, no LLM calls)</option>
              <option value="enhanced">Enhanced - HyDE + kNN (balanced)</option>
              <option value="agentic">Agentic - Multi-strategy (best recall, slowest)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Controls how SCG rules are retrieved for sanitization. Classic is fastest but less accurate. Agentic uses multiple LLM calls for maximum recall.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={(inputMode === 'text' ? !textInput.trim() : !uploadedFile) || isStreaming}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isStreaming ? (
              <>
                <ClockIcon className="w-5 h-5 mr-2 animate-spin" />
                Sanitizing Document...
              </>
            ) : (
              <>
                <DocumentTextIcon className="w-5 h-5 mr-2" />
                Start Tearline Process
              </>
            )}
          </button>
        </form>

      </div>
      
      {/* SCG Rule Hopping Graph Modal */}
      <SCGRuleHoppingGraph
        isOpen={showRuleHoppingGraph}
        onClose={closeRuleHoppingGraph}
        tearlineData={tearlineResult}
        segmentIndex={selectedSegmentIndex}
      />
    </div>
  );
}